import { eq, and, lte, isNull, isNotNull, gte, sql } from 'drizzle-orm';
import { db, runInTransaction } from '@/db/client';
import {
    recurringTransactions,
    accounts,
    creditCards,
    categories,
    transactions,
} from '@/db/schema';
import { generateId } from '@/lib/id';
import { createTransaction, hasActiveReservaYield, updateTransaction } from './transactionRepository';
import { getTagsForRecurring, setRecurringTags, setTransactionTags } from './tagRepository';
import { getSetting, setSetting } from './settingsRepository';
import { endOfMonth, format } from 'date-fns';
import { recalculateAccountBalance } from './accountRepository';
import { recalculateInvoiceTotal } from './invoiceRepository';
import { ExpectedError } from '@/lib/errorUtils';
import i18n from '@/lib/i18n';
import {
    getNextDate,
    anchorDayOf,
    inferFrequencyFromDates,
    deriveEndDateFromCount,
    nextOccurrence as advanceOccurrence,
    firstOccurrenceFrom,
    parseSkippedDates,
    advancePastSkipped,
    type RecurrenceRuleFields,
} from '@/lib/recurrence';
import { isVirtualTransactionId } from '@/lib/virtualTransactionId';
import { compareIso } from '@/lib/compareIso';

export type RecurringTransaction = typeof recurringTransactions.$inferSelect;

export type RecurringWithRelations = RecurringTransaction & {
    accountName?: string | null;
    accountColor?: string | null;
    accountIcon?: string | null;
    creditCardName?: string | null;
    creditCardColor?: string | null;
    categoryName?: string | null;
    categoryIcon?: string | null;
    categoryColor?: string | null;
};

export type UpcomingRecurringExpense = {
    id: string;
    source: 'recurring' | 'legacy_series';
    recurringId: string | null;
    amount: number;
    description: string;
    nextOccurrence: string;
    accountId?: string | null;
    accountName?: string | null;
    accountColor?: string | null;
    creditCardId?: string | null;
    creditCardName?: string | null;
    creditCardColor?: string | null;
    categoryId?: string | null;
    categoryName?: string | null;
    categoryIcon?: string | null;
    categoryColor?: string | null;
};

export type CreateRecurringData = {
    type: string;
    amount: number;
    description: string;
    /** Observação e hora da série (fonte de verdade na regra; ocorrências herdam). */
    notes?: string | null;
    time?: string | null;
    frequency: string;
    interval?: number;
    weekdays?: string | null;
    monthlyMode?: string | null;
    count?: number | null;
    startDate: string;
    endDate?: string | null;
    accountId?: string | null;
    creditCardId?: string | null;
    categoryId?: string | null;
    subcategoryId?: string | null;
    transferDestinationAccountId?: string | null;
    isHiddenFromSubs?: boolean;
    /**
     * Lançamento ignorado da SÉRIE (ADR 0169): fica na regra e toda ocorrência herda —
     * materializada e projetada. Fora da análise; dentro do saldo e da fatura (ADR 0037).
     */
    isIgnored?: boolean;
    // Aviso antecipado (ver docs/adr/0025). Quando undefined, é DERIVADO: liga sozinho em
    // despesa da categoria Assinaturas (marcador prático de "isto é serviço"). Um valor
    // explícito (toggle do usuário) sempre vence.
    advanceReminderEnabled?: boolean;
};

type CreateRecurringWithOccurrenceData = CreateRecurringData & {
    date: string;
    invoiceMonth?: string;
    isConfirmed?: boolean;
};

type ConvertTransactionToRecurringData = {
    transactionId: string;
    frequency: string;
    interval?: number;
    weekdays?: string | null;
    monthlyMode?: string | null;
    count?: number | null;
    endDate?: string | null;
};

type SettleOccurrenceData = {
    id: string;
    occurrenceDate?: string;
    date?: string;
    /** Hora do movimento real (abate de notificação bancária) — só nesta ocorrência. */
    time?: string;
    amount?: number;
    accountId?: string;
    /**
     * Paga no CARTÃO desta vez (ADR 0137): só ESTA ocorrência vai pra fatura, na data
     * real; a regra continua na conta. Nunca entra no patch de série, qualquer que
     * seja o `mode` — senão "esta e futuras" levaria a regra junto.
     */
    creditCardId?: string;
    /** Conta destino (só transferência) — permite ajustar o destino ao efetivar. */
    transferDestinationAccountId?: string;
    mode?: 'single' | 'future' | 'all';
};

// Nomes "bonitos" para os casos comuns (unidade base + interval). Fora deles,
// cai no genérico "A cada N <unidade>" — ver docs/features/RECURRENCE.md.
const niceFrequencyLabels: Record<string, string> = {
    'daily:1': 'Diária',
    'weekly:1': 'Semanal',
    'weekly:2': 'Quinzenal',
    'monthly:1': 'Mensal',
    'monthly:2': 'Bimestral',
    'monthly:3': 'Trimestral',
    'monthly:6': 'Semestral',
    'annual:1': 'Anual',
};

const baseUnitLabels: Record<string, { singular: string; plural: string }> = {
    daily: { singular: 'dia', plural: 'dias' },
    weekly: { singular: 'semana', plural: 'semanas' },
    monthly: { singular: 'mês', plural: 'meses' },
    annual: { singular: 'ano', plural: 'anos' },
};

// Mapeia compostos legados para a chave normalizada (defesa para dados antigos).
const legacyFrequencyKeys: Record<string, string> = {
    biweekly: 'weekly:2',
    bimonthly: 'monthly:2',
    quarterly: 'monthly:3',
    semiannual: 'monthly:6',
};

export function getFrequencyLabel(frequency: string, interval = 1): string {
    const key = legacyFrequencyKeys[frequency] ?? `${frequency}:${interval}`;
    if (niceFrequencyLabels[key]) {
        return niceFrequencyLabels[key];
    }

    const [unit, rawCount] = key.split(':');
    const count = Number(rawCount) || 1;
    const labels = baseUnitLabels[unit];
    if (!labels) {
        return frequency;
    }

    return `A cada ${count} ${count === 1 ? labels.singular : labels.plural}`;
}

export { getNextDate } from '@/lib/recurrence';

// A categoria "Assinaturas" (seed) é o marcador prático de "isto é serviço" usado para o
// Aviso antecipado nascer ligado. É sinal, não garantia (editável): o toggle manual cobre
// quem está fora dela. Ver docs/adr/0025.
const SUBSCRIPTION_CATEGORY_NAME = 'Assinaturas';

async function isSubscriptionExpenseCategory(categoryId: string | null | undefined): Promise<boolean> {
    if (!categoryId) return false;
    const rows = await db
        .select({ id: categories.id })
        .from(categories)
        .where(
            and(
                eq(categories.id, categoryId),
                eq(categories.name, SUBSCRIPTION_CATEGORY_NAME),
                eq(categories.type, 'expense'),
            ),
        );
    return rows.length > 0;
}

/**
 * Resolve o estado inicial do Aviso antecipado: valor explícito vence; senão deriva da
 * categoria (despesa em Assinaturas nasce ligado). Ver docs/adr/0025.
 */
async function resolveAdvanceReminderEnabled(
    explicit: boolean | undefined,
    type: string,
    categoryId: string | null | undefined,
): Promise<number> {
    if (explicit !== undefined) return explicit ? 1 : 0;
    if (type !== 'expense') return 0;
    return (await isSubscriptionExpenseCategory(categoryId)) ? 1 : 0;
}

export async function setAdvanceReminderEnabled(id: string, enabled: boolean): Promise<void> {
    await db
        .update(recurringTransactions)
        .set({ advanceReminderEnabled: enabled ? 1 : 0, updatedAt: new Date().toISOString() })
        .where(eq(recurringTransactions.id, id));
}

export async function getAll(): Promise<RecurringWithRelations[]> {
    const rows = await db
        .select({
            id: recurringTransactions.id,
            type: recurringTransactions.type,
            amount: recurringTransactions.amount,
            description: recurringTransactions.description,
            notes: recurringTransactions.notes,
            time: recurringTransactions.time,
            accountId: recurringTransactions.accountId,
            creditCardId: recurringTransactions.creditCardId,
            categoryId: recurringTransactions.categoryId,
            subcategoryId: recurringTransactions.subcategoryId,
            transferDestinationAccountId: recurringTransactions.transferDestinationAccountId,
            frequency: recurringTransactions.frequency,
            interval: recurringTransactions.interval,
            weekdays: recurringTransactions.weekdays,
            monthlyMode: recurringTransactions.monthlyMode,
            startDate: recurringTransactions.startDate,
            endDate: recurringTransactions.endDate,
            count: recurringTransactions.count,
            nextOccurrence: recurringTransactions.nextOccurrence,
            isActive: recurringTransactions.isActive,
            isHiddenFromSubs: recurringTransactions.isHiddenFromSubs,
            isIgnored: recurringTransactions.isIgnored,
            advanceReminderEnabled: recurringTransactions.advanceReminderEnabled,
            skippedDates: recurringTransactions.skippedDates,
            createdAt: recurringTransactions.createdAt,
            updatedAt: recurringTransactions.updatedAt,
            deletedAt: recurringTransactions.deletedAt,
            accountName: accounts.name,
            accountColor: accounts.color,
            accountIcon: accounts.icon,
            creditCardName: creditCards.name,
            creditCardColor: creditCards.color,
            categoryName: categories.name,
            categoryIcon: categories.icon,
            categoryColor: categories.color,
        })
        .from(recurringTransactions)
        .leftJoin(accounts, eq(recurringTransactions.accountId, accounts.id))
        .leftJoin(creditCards, eq(recurringTransactions.creditCardId, creditCards.id))
        .leftJoin(categories, eq(recurringTransactions.categoryId, categories.id))
        .where(isNull(recurringTransactions.deletedAt));

    return rows;
}

export async function getActive(): Promise<RecurringWithRelations[]> {
    const all = await getAll();
    return all.filter((r) => r.isActive === 1);
}

export async function getById(id: string): Promise<RecurringWithRelations | undefined> {
    const rows = await db
        .select({
            id: recurringTransactions.id,
            type: recurringTransactions.type,
            amount: recurringTransactions.amount,
            description: recurringTransactions.description,
            notes: recurringTransactions.notes,
            time: recurringTransactions.time,
            accountId: recurringTransactions.accountId,
            creditCardId: recurringTransactions.creditCardId,
            categoryId: recurringTransactions.categoryId,
            subcategoryId: recurringTransactions.subcategoryId,
            transferDestinationAccountId: recurringTransactions.transferDestinationAccountId,
            frequency: recurringTransactions.frequency,
            interval: recurringTransactions.interval,
            weekdays: recurringTransactions.weekdays,
            monthlyMode: recurringTransactions.monthlyMode,
            startDate: recurringTransactions.startDate,
            endDate: recurringTransactions.endDate,
            count: recurringTransactions.count,
            nextOccurrence: recurringTransactions.nextOccurrence,
            isActive: recurringTransactions.isActive,
            isHiddenFromSubs: recurringTransactions.isHiddenFromSubs,
            isIgnored: recurringTransactions.isIgnored,
            advanceReminderEnabled: recurringTransactions.advanceReminderEnabled,
            skippedDates: recurringTransactions.skippedDates,
            createdAt: recurringTransactions.createdAt,
            updatedAt: recurringTransactions.updatedAt,
            deletedAt: recurringTransactions.deletedAt,
            accountName: accounts.name,
            accountColor: accounts.color,
            accountIcon: accounts.icon,
            creditCardName: creditCards.name,
            creditCardColor: creditCards.color,
            categoryName: categories.name,
            categoryIcon: categories.icon,
            categoryColor: categories.color,
        })
        .from(recurringTransactions)
        .leftJoin(accounts, eq(recurringTransactions.accountId, accounts.id))
        .leftJoin(creditCards, eq(recurringTransactions.creditCardId, creditCards.id))
        .leftJoin(categories, eq(recurringTransactions.categoryId, categories.id))
        .where(and(eq(recurringTransactions.id, id), isNull(recurringTransactions.deletedAt)));

    return rows[0];
}

export async function create(data: CreateRecurringData): Promise<string> {
    const id = generateId();
    const now = new Date().toISOString();
    const interval = data.interval ?? 1;
    const weekdays = data.weekdays ?? null;
    const monthlyMode = data.monthlyMode ?? null;
    const count = data.count ?? null;
    const rule: RecurrenceRuleFields = { frequency: data.frequency, interval, startDate: data.startDate, weekdays, monthlyMode };
    const firstOcc = firstOccurrenceFrom(data.startDate, rule);
    const endDate = count != null
        ? deriveEndDateFromCount(rule, count)
        : (data.endDate ?? null);
    const advanceReminderEnabled = await resolveAdvanceReminderEnabled(
        data.advanceReminderEnabled,
        data.type,
        data.categoryId,
    );

    await db.insert(recurringTransactions).values({
        id,
        type: data.type,
        amount: data.amount,
        description: data.description,
        notes: data.notes ?? null,
        time: data.time ?? null,
        accountId: data.accountId ?? null,
        creditCardId: data.creditCardId ?? null,
        categoryId: data.categoryId ?? null,
        subcategoryId: data.subcategoryId ?? null,
        transferDestinationAccountId: data.transferDestinationAccountId ?? null,
        frequency: data.frequency,
        interval,
        weekdays,
        monthlyMode,
        count,
        startDate: data.startDate,
        endDate,
        nextOccurrence: firstOcc,
        isActive: endDate && firstOcc > endDate ? 0 : 1,
        isHiddenFromSubs: data.isHiddenFromSubs ? 1 : 0,
        isIgnored: data.isIgnored ? 1 : 0,
        advanceReminderEnabled,
        createdAt: now,
        updatedAt: now,
    });

    return id;
}

export async function createWithInitialOccurrence(
    data: CreateRecurringWithOccurrenceData,
): Promise<{ recurringId: string; transactionId: string }> {
    const recurringId = generateId();
    const now = new Date().toISOString();
    const today = format(new Date(), 'yyyy-MM-dd');
    const interval = data.interval ?? 1;
    const weekdays = data.weekdays ?? null;
    const monthlyMode = data.monthlyMode ?? null;
    const count = data.count ?? null;
    const rule: RecurrenceRuleFields = { frequency: data.frequency, interval, startDate: data.date, weekdays, monthlyMode };
    // 1ª ocorrência real (no multi-dia/nth-weekday pode não ser a própria data escolhida)
    const firstOcc = firstOccurrenceFrom(data.date, rule);
    const nextOccurrence = firstOcc > today
        ? firstOcc
        : advanceOccurrence(firstOcc, rule);
    const endDate = count != null
        ? deriveEndDateFromCount(rule, count)
        : (data.endDate ?? null);
    const advanceReminderEnabled = await resolveAdvanceReminderEnabled(
        data.advanceReminderEnabled,
        data.type,
        data.categoryId,
    );

    await db.insert(recurringTransactions).values({
        id: recurringId,
        type: data.type,
        amount: data.amount,
        description: data.description,
        notes: data.notes ?? null,
        time: data.time ?? null,
        accountId: data.accountId ?? null,
        creditCardId: data.creditCardId ?? null,
        categoryId: data.categoryId ?? null,
        subcategoryId: data.subcategoryId ?? null,
        transferDestinationAccountId: data.transferDestinationAccountId ?? null,
        frequency: data.frequency,
        interval,
        weekdays,
        monthlyMode,
        count,
        startDate: data.date,
        endDate,
        nextOccurrence,
        isActive: endDate && nextOccurrence > endDate ? 0 : 1,
        isHiddenFromSubs: data.isHiddenFromSubs ? 1 : 0,
        isIgnored: data.isIgnored ? 1 : 0,
        advanceReminderEnabled,
        createdAt: now,
        updatedAt: now,
    });

    // 1ª ocorrência confirmada é pagamento acontecendo (o createTransaction carimba a
    // hora); pendente é agendamento e nasce sem hora — a virada pendente→pago carimba
    // depois. Normalizado a boolean: `isConfirmed` omitido conta como pago, mas 1ª
    // ocorrência FUTURA nunca nasce paga.
    const firstOccConfirmed = (data.isConfirmed ?? true) && firstOcc <= today;
    const transaction = await createTransaction({
        type: data.type,
        amount: data.amount,
        description: data.description,
        date: firstOcc,
        time: data.time ?? undefined,
        notes: data.notes ?? undefined,
        accountId: data.accountId ?? undefined,
        creditCardId: data.creditCardId ?? undefined,
        invoiceMonth: data.invoiceMonth,
        categoryId: data.categoryId ?? undefined,
        subcategoryId: data.subcategoryId ?? undefined,
        transferDestinationAccountId: data.transferDestinationAccountId ?? undefined,
        isConfirmed: firstOccConfirmed,
        // A 1ª ocorrência nasce com o Ignorado da série, como as seguintes (ADR 0169).
        isIgnored: !!data.isIgnored,
        isRecurring: true,
        recurringId,
    });

    return { recurringId, transactionId: transaction.id };
}

export async function convertTransactionToRecurring(
    data: ConvertTransactionToRecurringData,
): Promise<{ recurringId: string; transactionId: string }> {
    // Id virtual (Ocorrência projetada) não tem linha pra converter — e a ocorrência já
    // pertence a uma regra. Recusar na porta mantém o erro legível: sem isto, o select
    // vazio virava "Transação não encontrada", indistinguível de um lançamento excluído
    // (foi assim que o bug do detalhe órfão chegou ao Sentry disfarçado). A tela nunca
    // deve chegar aqui — ver resolveExistingTransactionSubmitPath. Erro comum (não
    // ExpectedError): é violação de invariante, tem que aparecer no Sentry. A mensagem é
    // curta de propósito — o MutationCache global mostra `error.message` cru ao usuário.
    if (isVirtualTransactionId(data.transactionId)) {
        throw new Error('Ocorrência projetada já pertence a uma recorrência.');
    }

    const [transaction] = await db
        .select()
        .from(transactions)
        .where(and(eq(transactions.id, data.transactionId), isNull(transactions.deletedAt)));

    if (!transaction) {
        throw new Error('Transação não encontrada');
    }

    if (transaction.installmentId) {
        throw new ExpectedError(i18n.t('ui:errors.installmentPlanNotConvertible'));
    }

    if (transaction.recurringId) {
        return { recurringId: transaction.recurringId, transactionId: transaction.id };
    }

    // Rendimento de caixinha (ADR 0161) é da caixinha: virar recorrência criaria receitas futuras
    // soltas, sem caixinha nenhuma (QA de 07/10/2026). A tela já esconde a Repetição nesse caso.
    if (await hasActiveReservaYield([transaction.id])) {
        throw new ExpectedError(i18n.t('ui:errors.reservaYieldManagedInReserva'));
    }

    const recurringId = generateId();
    const now = new Date().toISOString();
    const today = format(new Date(), 'yyyy-MM-dd');
    const interval = data.interval ?? 1;
    const weekdays = data.weekdays ?? null;
    const monthlyMode = data.monthlyMode ?? null;
    const count = data.count ?? null;
    const rule: RecurrenceRuleFields = { frequency: data.frequency, interval, startDate: transaction.date, weekdays, monthlyMode };
    const nextOccurrence = transaction.date > today
        ? transaction.date
        : advanceOccurrence(transaction.date, rule);
    const endDate = count != null
        ? deriveEndDateFromCount(rule, count)
        : (data.endDate ?? null);
    // Mesmo default-on por categoria que create/createWithInitialOccurrence (ver docs/adr/0025):
    // converter um serviço em Assinaturas já nasce com o Aviso antecipado ligado.
    const advanceReminderEnabled = await resolveAdvanceReminderEnabled(
        undefined,
        transaction.type,
        transaction.categoryId,
    );

    await db.insert(recurringTransactions).values({
        id: recurringId,
        type: transaction.type,
        amount: transaction.amount,
        description: transaction.description,
        notes: transaction.notes ?? null,
        // A hora da transação NÃO vira hora da série: desde o ADR 0080 todo pagamento
        // tem hora carimbada, então copiá-la gravaria "a hora do pagamento anterior" em
        // TODAS as ocorrências futuras — que devem nascer sem hora e carimbar a própria
        // virada pendente→pago. Hora de série só existe por escolha explícita na regra.
        time: null,
        accountId: transaction.accountId ?? null,
        creditCardId: transaction.creditCardId ?? null,
        categoryId: transaction.categoryId ?? null,
        subcategoryId: transaction.subcategoryId ?? null,
        transferDestinationAccountId: transaction.transferDestinationAccountId ?? null,
        frequency: data.frequency,
        interval,
        weekdays,
        monthlyMode,
        count,
        startDate: transaction.date,
        endDate,
        nextOccurrence,
        isActive: endDate && nextOccurrence > endDate ? 0 : 1,
        isHiddenFromSubs: transaction.isHiddenFromSubs,
        // Converter um lançamento ignorado em série: a série inteira nasce ignorada — as
        // próximas ocorrências herdam o que o usuário já tinha marcado nele (ADR 0169).
        isIgnored: transaction.isIgnored === 1 ? 1 : 0,
        advanceReminderEnabled,
        createdAt: now,
        updatedAt: now,
    });

    await db
        .update(transactions)
        .set({
            recurringId,
            isRecurring: 1,
            updatedAt: now,
        })
        .where(eq(transactions.id, transaction.id));

    return { recurringId, transactionId: transaction.id };
}

/**
 * Aplica `tagIds` à série respeitando o escopo (ver CONTEXT.md "Tag numa recorrência"):
 * - 'all'    : grava nas tags da REGRA (default das futuras ocorrências) e em TODAS as
 *              ocorrências materializadas da série.
 * - 'future' : grava na REGRA e nas ocorrências materializadas com slot >= `fromDate`.
 * - 'single' : NÃO toca na regra; grava só na ocorrência do slot == `fromDate` (vira Exceção).
 * `fromDate` é o slot agendado (`recurringOccurrenceDate`) da ocorrência editada.
 */
export async function setRecurringSeriesTags(params: {
    recurringId: string;
    fromDate: string;
    mode: 'single' | 'future' | 'all';
    tagIds: string[];
}): Promise<void> {
    const { recurringId, fromDate, mode, tagIds } = params;

    // Regra = default das futuras. 'single' é uma Exceção da ocorrência, não mexe na regra.
    if (mode !== 'single') {
        await setRecurringTags(recurringId, tagIds);
    }

    const slot = sql<string>`COALESCE(${transactions.recurringOccurrenceDate}, ${transactions.date})`;
    const conditions = [eq(transactions.recurringId, recurringId), isNull(transactions.deletedAt)];
    if (mode === 'future') conditions.push(gte(slot, fromDate));
    if (mode === 'single') conditions.push(eq(slot, fromDate));

    const rows = await db.select({ id: transactions.id }).from(transactions).where(and(...conditions));
    for (const row of rows) {
        await setTransactionTags(row.id, tagIds);
    }
}

export async function update(
    id: string,
    data: Partial<CreateRecurringData> & { nextOccurrence?: string; isActive?: number },
): Promise<void> {
    const [existing] = await db
        .select()
        .from(recurringTransactions)
        .where(and(eq(recurringTransactions.id, id), isNull(recurringTransactions.deletedAt)));

    if (!existing) {
        return;
    }

    const now = new Date().toISOString();
    const today = format(new Date(), 'yyyy-MM-dd');
    const mergedStartDate = data.startDate ?? existing.startDate;
    const mergedFrequency = data.frequency ?? existing.frequency;
    const mergedInterval = data.interval ?? existing.interval;
    const mergedWeekdays = data.weekdays !== undefined ? data.weekdays : existing.weekdays;
    const mergedMonthlyMode = data.monthlyMode !== undefined ? data.monthlyMode : existing.monthlyMode;
    const mergedRule: RecurrenceRuleFields = {
        frequency: mergedFrequency,
        interval: mergedInterval,
        startDate: mergedStartDate,
        weekdays: mergedWeekdays,
        monthlyMode: mergedMonthlyMode,
    };
    // `count` é a intenção de autoria; quando presente, o endDate é DERIVADO dele
    // (ignora qualquer endDate passado). Caso contrário, endDate é "por data"/"nunca".
    // Ver docs/features/RECURRENCE.md (invariante do count).
    const resolvedCount = data.count !== undefined ? data.count : existing.count;
    const mergedEndDate = resolvedCount != null
        ? deriveEndDateFromCount(mergedRule, resolvedCount)
        : (data.endDate !== undefined ? data.endDate : existing.endDate);
    const scheduleChanged =
        data.startDate !== undefined
        || data.frequency !== undefined
        || data.interval !== undefined
        || data.weekdays !== undefined
        || data.monthlyMode !== undefined
        || data.endDate !== undefined
        || data.count !== undefined;
    const ruleShapeChanged =
        (data.type !== undefined && data.type !== existing.type)
        || (data.amount !== undefined && data.amount !== existing.amount)
        || (data.description !== undefined && data.description !== existing.description)
        || (data.notes !== undefined && (data.notes ?? null) !== existing.notes)
        || (data.time !== undefined && (data.time ?? null) !== existing.time)
        || (data.startDate !== undefined && data.startDate !== existing.startDate)
        || (data.frequency !== undefined && data.frequency !== existing.frequency)
        || (data.interval !== undefined && data.interval !== existing.interval)
        || (data.weekdays !== undefined && (data.weekdays ?? null) !== existing.weekdays)
        || (data.monthlyMode !== undefined && (data.monthlyMode ?? null) !== existing.monthlyMode)
        || (mergedEndDate !== existing.endDate)
        || (data.accountId !== undefined && (data.accountId ?? null) !== existing.accountId)
        || (data.creditCardId !== undefined && (data.creditCardId ?? null) !== existing.creditCardId)
        || (data.categoryId !== undefined && (data.categoryId ?? null) !== existing.categoryId)
        || (data.subcategoryId !== undefined && (data.subcategoryId ?? null) !== existing.subcategoryId)
        || (
            data.transferDestinationAccountId !== undefined
            && (data.transferDestinationAccountId ?? null) !== existing.transferDestinationAccountId
        )
        // Ignorar/reincluir a série refaz a cauda intocada: as pendentes futuras renascem
        // com o flag novo, como numa troca de categoria (ADR 0169).
        || (data.isIgnored !== undefined && (data.isIgnored ? 1 : 0) !== existing.isIgnored);
    const floorDate = [today, existing.nextOccurrence, mergedStartDate].sort().at(-1)!;
    const nextOccurrence = data.nextOccurrence
        ?? (scheduleChanged
            ? firstOccurrenceFrom(floorDate, mergedRule)
            : existing.nextOccurrence);
    const isActive = mergedEndDate && nextOccurrence > mergedEndDate
        ? 0
        : (data.isActive ?? existing.isActive);
    const updateData: Record<string, unknown> = {
        ...data,
        // endDate e count são resolvidos juntos (count manda; deriva o endDate).
        count: resolvedCount,
        endDate: mergedEndDate,
        nextOccurrence,
        isActive,
        updatedAt: now,
    };

    if (data.isHiddenFromSubs !== undefined) {
        updateData.isHiddenFromSubs = data.isHiddenFromSubs ? 1 : 0;
    }

    if (data.isIgnored !== undefined) {
        updateData.isIgnored = data.isIgnored ? 1 : 0;
    }

    if (data.advanceReminderEnabled !== undefined) {
        updateData.advanceReminderEnabled = data.advanceReminderEnabled ? 1 : 0;
    }

    if (ruleShapeChanged) {
        const firstFutureDate = getNextDate(today, 'daily');
        // Fix #6 (auditoria jul/2026): re-materializar a cauda com a forma nova NÃO pode apagar
        // o que o usuário já tocou. Só caem as PROJEÇÕES INTACTAS: não-confirmadas, com os
        // valores da própria regra antiga (uma ocorrência confirmada é pagamento registrado —
        // apagá-la sumia com o lançamento do extrato e do saldo; uma Exceção — valor/descrição
        // próprios — é edição deliberada) E com a data real NO próprio slot: uma ocorrência
        // REMARCADA pra outro dia (mesmo tipo/valor/descrição) também é edição do usuário —
        // sem essa condição ela era apagada e re-materializada na data padrão, revertendo a
        // remarcação em silêncio. E o corte é pelo SLOT (recurring_occurrence_date), não pela
        // data real: uma ocorrência de slot passado adiada pra amanhã não é da cauda.
        // As linhas preservadas ocupam o slot delas — generateOccurrences dedupa por slot e
        // não duplica. Limitação aceita: Exceção que só mudou categoria/conta não é detectada
        // (é recriada com a forma nova).
        const untouchedFutureProjection = and(
            eq(transactions.recurringId, id),
            isNull(transactions.deletedAt),
            sql`COALESCE(${transactions.recurringOccurrenceDate}, ${transactions.date}) >= ${firstFutureDate}`,
            sql`${transactions.date} = COALESCE(${transactions.recurringOccurrenceDate}, ${transactions.date})`,
            eq(transactions.isConfirmed, 0),
            eq(transactions.type, existing.type),
            eq(transactions.amount, existing.amount),
            eq(transactions.description, existing.description),
        );
        const futureOccurrences = await db
            .select({
                id: transactions.id,
                accountId: transactions.accountId,
                transferDestinationAccountId: transactions.transferDestinationAccountId,
                invoiceId: transactions.invoiceId,
            })
            .from(transactions)
            .where(untouchedFutureProjection);

        if (futureOccurrences.length > 0) {
            await db
                .update(transactions)
                .set({
                    deletedAt: now,
                    updatedAt: now,
                })
                .where(untouchedFutureProjection);

            await recalculateMigrationSideEffects(
                futureOccurrences.flatMap((row) => [
                    row.accountId ?? '',
                    row.transferDestinationAccountId ?? '',
                ]).filter(Boolean),
                futureOccurrences.map((row) => row.invoiceId ?? '').filter(Boolean),
            );
        }
    }

    await db
        .update(recurringTransactions)
        .set(updateData)
        .where(eq(recurringTransactions.id, id));
}

export async function togglePause(id: string): Promise<void> {
    const existing = await db
        .select({ isActive: recurringTransactions.isActive })
        .from(recurringTransactions)
        .where(eq(recurringTransactions.id, id));

    if (!existing[0]) return;

    await db
        .update(recurringTransactions)
        .set({
            isActive: existing[0].isActive === 1 ? 0 : 1,
            updatedAt: new Date().toISOString(),
        })
        .where(eq(recurringTransactions.id, id));
}

export async function remove(id: string): Promise<void> {
    const now = new Date().toISOString();
    await db
        .update(recurringTransactions)
        .set({ deletedAt: now, isActive: 0, updatedAt: now })
        .where(eq(recurringTransactions.id, id));
}

async function recalculateMigrationSideEffects(
    accountIds: Iterable<string>,
    invoiceIds: Iterable<string>,
): Promise<void> {
    for (const accountId of new Set(accountIds)) {
        await recalculateAccountBalance(accountId);
    }
    for (const invoiceId of new Set(invoiceIds)) {
        await recalculateInvoiceTotal(invoiceId);
    }
}

async function linkOrphanStartOccurrences(): Promise<number> {
    const now = new Date().toISOString();
    let linked = 0;

    const recurrings = await db
        .select()
        .from(recurringTransactions)
        .where(isNull(recurringTransactions.deletedAt));

    for (const recurring of recurrings) {
        const existing = await findExistingOccurrence(recurring.id, recurring.startDate);
        if (existing) {
            continue;
        }

        const candidateConditions = [
            eq(transactions.date, recurring.startDate),
            eq(transactions.type, recurring.type),
            eq(transactions.amount, recurring.amount),
            eq(transactions.description, recurring.description),
            isNull(transactions.deletedAt),
            isNull(transactions.installmentId),
            isNull(transactions.recurringId),
            recurring.accountId
                ? eq(transactions.accountId, recurring.accountId)
                : isNull(transactions.accountId),
            recurring.creditCardId
                ? eq(transactions.creditCardId, recurring.creditCardId)
                : isNull(transactions.creditCardId),
            recurring.transferDestinationAccountId
                ? eq(transactions.transferDestinationAccountId, recurring.transferDestinationAccountId)
                : isNull(transactions.transferDestinationAccountId),
        ];

        const candidates = await db
            .select({ id: transactions.id })
            .from(transactions)
            .where(and(...candidateConditions));

        if (candidates.length !== 1) {
            continue;
        }

        await db
            .update(transactions)
            .set({
                recurringId: recurring.id,
                isRecurring: 1,
                updatedAt: now,
            })
            .where(eq(transactions.id, candidates[0].id));

        if (recurring.nextOccurrence === recurring.startDate && recurring.startDate <= format(new Date(), 'yyyy-MM-dd')) {
            await db
                .update(recurringTransactions)
                .set({
                    nextOccurrence: advanceOccurrence(recurring.startDate, recurring),
                    updatedAt: now,
                })
                .where(eq(recurringTransactions.id, recurring.id));
        }

        linked += 1;
    }

    return linked;
}

export async function migrateLegacyRecurringData(options: { force?: boolean } = {}): Promise<{
    createdRules: number;
    linkedTransactions: number;
    cleanedFutureTransactions: number;
}> {
    // Migração de dados LEGADOS (lançamentos recorrentes de antes das regras).
    // Dado novo nunca nasce nesse formato, então depois de uma passada completa
    // não há o que migrar — mas ela rodava em TODO cold start (SELECT de todos os
    // recorrentes + mapeamento drizzle: ~42 ms numa base de 2,9 mil transações no
    // emulador, perfil de 09/set/2026). Flag em `settings` marca a conclusão, no
    // mesmo padrão de `advance_reminder_backfill_v1` em db/client.ts.
    // `force` é o caminho do import de backup (lib/backupService.ts): um arquivo
    // antigo pode trazer dado legado E a própria flag restaurada — tem que rodar.
    const DONE_KEY = 'legacy_recurring_migration_v1';
    if (!options.force && (await getSetting(DONE_KEY)) === '1') {
        return { createdRules: 0, linkedTransactions: 0, cleanedFutureTransactions: 0 };
    }

    const today = format(new Date(), 'yyyy-MM-dd');
    const now = new Date().toISOString();
    let createdRules = 0;
    let cleanedFutureTransactions = 0;

    const existingRules = await db
        .select({ id: recurringTransactions.id })
        .from(recurringTransactions);
    const existingRuleIds = new Set(existingRules.map((row) => row.id));

    const legacyRows = await db
        .select({
            id: transactions.id,
            type: transactions.type,
            amount: transactions.amount,
            description: transactions.description,
            date: transactions.date,
            accountId: transactions.accountId,
            creditCardId: transactions.creditCardId,
            categoryId: transactions.categoryId,
            subcategoryId: transactions.subcategoryId,
            transferDestinationAccountId: transactions.transferDestinationAccountId,
            invoiceId: transactions.invoiceId,
            isConfirmed: transactions.isConfirmed,
            isHiddenFromSubs: transactions.isHiddenFromSubs,
            recurringId: transactions.recurringId,
            recurringOccurrenceDate: transactions.recurringOccurrenceDate,
            createdAt: transactions.createdAt,
        })
        .from(transactions)
        .where(
            and(
                eq(transactions.isRecurring, 1),
                isNull(transactions.installmentId),
                isNull(transactions.deletedAt),
                sql`${transactions.recurringId} IS NOT NULL`,
            ),
        );

    const legacyGroups = new Map<string, typeof legacyRows>();
    for (const row of legacyRows) {
        const recurringId = row.recurringId;
        if (!recurringId || existingRuleIds.has(recurringId)) {
            continue;
        }

        // Só reconstrói regra de dado GENUINAMENTE legado (pré-rastreio de ocorrência).
        // Uma transação com recurringOccurrenceDate foi criada pelo sistema moderno de
        // regras — se está "sem regra", é uma órfã intencional de um split "desta em
        // diante" (a regra antiga foi soft-deletada e fica de fora do backup). Reconstruí-la
        // RESSUSCITA a regra morta como ativa → duplicação após import. Ver ADR-0007.
        if (row.recurringOccurrenceDate) {
            continue;
        }

        const group = legacyGroups.get(recurringId) ?? [];
        group.push(row);
        legacyGroups.set(recurringId, group);
    }

    const affectedAccountIds = new Set<string>();
    const affectedInvoiceIds = new Set<string>();

    for (const [recurringId, rows] of legacyGroups) {
        const sorted = [...rows].sort((left, right) => {
            if (left.date !== right.date) return compareIso(left.date, right.date);
            return compareIso(left.createdAt, right.createdAt);
        });
        const inferred = inferFrequencyFromDates(sorted.map((row) => row.date));
        if (!inferred) {
            continue;
        }

        const futureRows = sorted.filter((row) => row.date > today);
        const keptRows = sorted.filter((row) => row.date <= today);
        const latestKeptRow = keptRows[keptRows.length - 1] ?? null;
        const templateRow = latestKeptRow ?? sorted[0];

        let nextOccurrence = futureRows[0]?.date
            ?? getNextDate(templateRow.date, inferred.frequency, inferred.interval, anchorDayOf(sorted[0].date));
        let rowsToDelete = futureRows;

        if (keptRows.length === 0 && sorted.length > 0) {
            const [firstFutureRow, ...remainingFutureRows] = sorted;
            nextOccurrence = firstFutureRow.date;
            rowsToDelete = remainingFutureRows;
        }

        await db.insert(recurringTransactions).values({
            id: recurringId,
            type: templateRow.type,
            amount: templateRow.amount,
            description: templateRow.description,
            accountId: templateRow.accountId ?? null,
            creditCardId: templateRow.creditCardId ?? null,
            categoryId: templateRow.categoryId ?? null,
            subcategoryId: templateRow.subcategoryId ?? null,
            transferDestinationAccountId: templateRow.transferDestinationAccountId ?? null,
            frequency: inferred.frequency,
            interval: inferred.interval,
            weekdays: null,
            monthlyMode: null,
            count: null,
            startDate: sorted[0].date,
            endDate: null,
            nextOccurrence,
            isActive: 1,
            isHiddenFromSubs: sorted.some((row) => row.isHiddenFromSubs === 1) ? 1 : 0,
            // Deriva o default-on por categoria aqui também: o backfill (db/client.ts) roda ANTES
            // desta migração e já travou seu guard, então séries de Assinaturas reconstruídas aqui
            // nunca seriam pegas por ele. Derivando no insert, a ordem de boot deixa de importar.
            advanceReminderEnabled: await resolveAdvanceReminderEnabled(
                undefined,
                templateRow.type,
                templateRow.categoryId,
            ),
            createdAt: sorted[0].createdAt,
            updatedAt: now,
        });

        existingRuleIds.add(recurringId);
        createdRules += 1;

        for (const row of rowsToDelete) {
            await db
                .update(transactions)
                .set({ deletedAt: now, updatedAt: now })
                .where(eq(transactions.id, row.id));

            if (row.accountId) affectedAccountIds.add(row.accountId);
            if (row.transferDestinationAccountId) affectedAccountIds.add(row.transferDestinationAccountId);
            if (row.invoiceId) affectedInvoiceIds.add(row.invoiceId);
            cleanedFutureTransactions += 1;
        }
    }

    const linkedTransactions = await linkOrphanStartOccurrences();
    await recalculateMigrationSideEffects(affectedAccountIds, affectedInvoiceIds);
    // Só marca depois da passada inteira: processo morto no meio roda de novo.
    await setSetting(DONE_KEY, '1');

    return { createdRules, linkedTransactions, cleanedFutureTransactions };
}

async function findExistingOccurrence(recurringId: string, date: string) {
    // Deduplica pelo SLOT AGENDADO (recurringOccurrenceDate), não pela data real
    // (transactions.date) — que é editável. Se o usuário antecipa/adia uma ocorrência
    // (muda a data real), o slot continua o mesmo e o dedup não fura, evitando que
    // generateOccurrences regenere a ocorrência. Mesma chave usada pela projeção
    // (ver transactionRepository.ts). Fallback p/ `date` em linhas antigas (pré-slot).
    const occurrenceSlot = sql<string>`COALESCE(${transactions.recurringOccurrenceDate}, ${transactions.date})`;
    const rows = await db
        .select({
            id: transactions.id,
            isConfirmed: transactions.isConfirmed,
        })
        .from(transactions)
        .where(
            and(
                eq(transactions.recurringId, recurringId),
                eq(occurrenceSlot, date),
                isNull(transactions.deletedAt),
            ),
        );

    return rows[0];
}

async function ensureOccurrence(
    rec: RecurringWithRelations,
    date: string,
    isConfirmed: boolean,
): Promise<string> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const actualDate = isConfirmed && date > today ? today : date;

    const existingOccurrence = await findExistingOccurrence(rec.id, date);

    if (existingOccurrence) {
        if (isConfirmed && existingOccurrence.isConfirmed !== 1) {
            await updateTransaction(existingOccurrence.id, {
                isConfirmed: true,
                ...(date > today ? { date: today } : {}),
            });
        }
        return existingOccurrence.id;
    }

    const transaction = await createTransaction({
        type: rec.type,
        amount: rec.amount,
        description: rec.description,
        // Observação e hora da série: a ocorrência nasce com a nota/hora da REGRA (fonte
        // de verdade), igual às tags — sem isto valiam só na 1ª ocorrência (dor relatada).
        notes: rec.notes ?? undefined,
        time: rec.time ?? undefined,
        date: actualDate,
        // O slot é a data AGENDADA (`date`), mesmo quando a data real (`actualDate`) é
        // antecipada p/ hoje numa confirmação. Sem isso, o slot herdaria a data real e o
        // dedup por slot (findExistingOccurrence) furaria. Ver Fix #2 no troubleshooting.
        recurringOccurrenceDate: date,
        accountId: rec.accountId ?? undefined,
        creditCardId: rec.creditCardId ?? undefined,
        categoryId: rec.categoryId ?? undefined,
        subcategoryId: rec.subcategoryId ?? undefined,
        transferDestinationAccountId: rec.type === 'transfer' ? (rec.transferDestinationAccountId ?? undefined) : undefined,
        // Materializar pendente é REGISTRO, não pagamento: o createTransaction só carimba
        // hora em criação CONFIRMADA (payOccurrence), então o slot de hoje nasce sem hora
        // e a virada pendente→pago carimba a efetivação de verdade.
        isConfirmed,
        // Ignorado da SÉRIE (ADR 0169): a ocorrência nasce com o flag da regra, como a nota.
        isIgnored: rec.isIgnored === 1,
        isRecurring: true,
        recurringId: rec.id,
    });

    // Herda as tags da REGRA (série) na ocorrência recém-criada. É isto que faz a tag valer
    // em TODAS as ocorrências, não só na 1ª. Só no caminho de criação — ocorrência já
    // existente mantém suas tags (pode ser uma Exceção). Ver CONTEXT.md ("Tag numa recorrência").
    const ruleTagIds = (await getTagsForRecurring(rec.id)).map((tag) => tag.id);
    if (ruleTagIds.length > 0) {
        await setTransactionTags(transaction.id, ruleTagIds);
    }

    return transaction.id;
}

export async function materializeOccurrence(id: string, occurrenceDate?: string): Promise<string | null> {
    const rec = await getById(id);
    if (!rec || !rec.isActive) return null;

    const targetDate = occurrenceDate ?? rec.nextOccurrence;
    // NÃO avança nextOccurrence aqui. Este helper só "concretiza" uma ocorrência
    // projetada para que uma edição (renomear, mudar categoria) tenha uma linha real
    // onde se ancorar — não é um pagamento. Avançar a agenda a cada edição era a causa
    // do bug em que renomear uma assinatura empurrava a cobrança um período (e empilhava
    // cobranças). A dedup por slot (findExistingOccurrence) garante que generateOccurrences
    // não duplique e reavance nextOccurrence sozinho. Confirmar/pagar usa settleOccurrence,
    // que tem avanço próprio. Ver docs/RECURRING_CREDIT_CARD_TROUBLESHOOTING.md (achados 1 e 2).
    return ensureOccurrence(rec, targetDate, false);
}

/**
 * Pula uma única ocorrência de uma regra sem afetar o resto da série (Exceção por
 * exclusão — ver docs/adr/0014). Registra a data em `skippedDates`; se a ocorrência já
 * estava materializada, faz soft-delete dela; se o slot pulado era o `nextOccurrence`,
 * avança o ponteiro para a próxima ocorrência não-pulada. As três engines de projeção/
 * geração leem `skippedDates` e omitem o slot, então ele some de todas as superfícies.
 */
export async function skipOccurrence(recurringId: string, occurrenceDate: string): Promise<void> {
    const [rule] = await db
        .select()
        .from(recurringTransactions)
        .where(and(eq(recurringTransactions.id, recurringId), isNull(recurringTransactions.deletedAt)));
    if (!rule) return;

    const skipped = parseSkippedDates(rule.skippedDates);
    skipped.add(occurrenceDate);
    const serialized = JSON.stringify([...skipped].sort());

    // Avança nextOccurrence se o slot pulado for o da frente, para a "próxima cobrança"
    // não apontar para uma ocorrência que não vai acontecer.
    const newNextOccurrence = occurrenceDate === rule.nextOccurrence
        ? advancePastSkipped(rule, rule.nextOccurrence, skipped)
        : rule.nextOccurrence;
    const isActive = rule.endDate && newNextOccurrence > rule.endDate ? 0 : rule.isActive;

    // 1) Soft-delete de qualquer ocorrência materializada (não-deletada) naquele slot, e
    // recalcula saldos/faturas afetados. Cobre uma ocorrência futura já materializada (ex.:
    // editada antes) — sem isso ela continuaria contando e/ou seria reprojetada.
    //
    // Feito ANTES de gravar a regra: como não há transação envolvendo as duas escritas, se o
    // recálculo falhar no meio é melhor a regra ainda NÃO ter marcado o slot como pulado — o
    // usuário só vê o skip não ter pego e repete, em vez de ficar com uma cobrança-fantasma
    // (linha viva contando no total, mas slot já pulado e some da projeção).
    const slotMatch = and(
        eq(transactions.recurringId, recurringId),
        eq(sql`COALESCE(${transactions.recurringOccurrenceDate}, ${transactions.date})`, occurrenceDate),
        isNull(transactions.deletedAt),
    );
    const materialized = await db.select().from(transactions).where(slotMatch);
    if (materialized.length > 0) {
        const now = new Date().toISOString();
        await db.update(transactions).set({ deletedAt: now, updatedAt: now }).where(slotMatch);

        const affectedAccounts = new Set<string>();
        const affectedInvoices = new Set<string>();
        for (const tx of materialized) {
            if (tx.accountId) affectedAccounts.add(tx.accountId);
            if (tx.transferDestinationAccountId) affectedAccounts.add(tx.transferDestinationAccountId);
            if (tx.invoiceId) affectedInvoices.add(tx.invoiceId);
        }
        for (const accountId of affectedAccounts) await recalculateAccountBalance(accountId);
        for (const invoiceId of affectedInvoices) await recalculateInvoiceTotal(invoiceId);
    }

    // 2) Grava a regra (slot pulado + ponteiro avançado) como "commit" da intenção.
    await db
        .update(recurringTransactions)
        .set({
            skippedDates: serialized,
            nextOccurrence: newNextOccurrence,
            isActive,
            updatedAt: new Date().toISOString(),
        })
        .where(eq(recurringTransactions.id, recurringId));
}

/**
 * Manually pay the next occurrence of a recurring transaction.
 * Creates a confirmed transaction for the current nextOccurrence date
 * and advances the nextOccurrence date.
 */
export async function payOccurrence(id: string, occurrenceDate?: string): Promise<void> {
    const rec = await getById(id);
    if (!rec || !rec.isActive) return;
    const targetDate = occurrenceDate ?? rec.nextOccurrence;

    await ensureOccurrence(rec, targetDate, true);

    if (targetDate !== rec.nextOccurrence) {
        return;
    }

    // Próxima data, pulando slots marcados como pulados (skippedDates) para o ponteiro
    // "próxima cobrança" não cair numa ocorrência que não vai acontecer.
    const skipped = parseSkippedDates(rec.skippedDates);
    let next = advancePastSkipped(rec, advanceOccurrence(targetDate, rec), skipped);

    // Check if endDate has been passed
    let isActive = 1;
    if (rec.endDate && next > rec.endDate) {
        isActive = 0;
    }

    // Update recurring
    await db
        .update(recurringTransactions)
        .set({
            nextOccurrence: next,
            isActive,
            updatedAt: new Date().toISOString(),
        })
        .where(eq(recurringTransactions.id, rec.id));
}

export async function settleOccurrence({
    id,
    occurrenceDate,
    date,
    time,
    amount,
    accountId,
    creditCardId,
    transferDestinationAccountId,
    mode = 'single',
}: SettleOccurrenceData): Promise<string | null> {
    const rec = await getById(id);
    if (!rec || !rec.isActive) return null;

    const targetDate = occurrenceDate ?? rec.nextOccurrence;
    const today = format(new Date(), 'yyyy-MM-dd');
    const actualDate = date ?? (targetDate > today ? today : targetDate);
    const seriesPatch: Parameters<typeof updateTransaction>[1] = {};

    if (amount !== undefined) seriesPatch.amount = amount;
    // Conta e Cartão são excludentes na linha: paga no cartão, a conta da notificação não entra.
    if (accountId !== undefined && !creditCardId) seriesPatch.accountId = accountId;
    if (transferDestinationAccountId !== undefined) {
        seriesPatch.transferDestinationAccountId = transferDestinationAccountId;
    }

    // Procura a ocorrência já materializada pelo SLOT agendado (mesma chave de
    // findExistingOccurrence), não pela data real — que é editável. Sem isto, pagar um slot
    // cuja data real foi movida (ex.: materializado para uma edição) não acha a linha e cria
    // uma DUPLICATA confirmada. Ver troubleshooting (achado: editar-depois-pagar).
    const existingOccurrence = await findExistingOccurrence(rec.id, targetDate);

    const occurrence =
        existingOccurrence
        ?? await createTransaction({
            type: rec.type,
            amount: rec.amount,
            description: rec.description,
            notes: rec.notes ?? undefined,
            time: rec.time ?? undefined,
            date: targetDate,
            // O slot é a data AGENDADA, igual ao ensureOccurrence. Aqui é ainda mais
            // crítico: quando o abate vem de uma notificação a data real quase sempre
            // difere do slot (dinheiro que caiu adiantado), e sem gravá-lo o dedup por
            // slot passaria a olhar a data real e regeneraria a ocorrência.
            recurringOccurrenceDate: targetDate,
            accountId: rec.accountId ?? undefined,
            creditCardId: rec.creditCardId ?? undefined,
            categoryId: rec.categoryId ?? undefined,
            subcategoryId: rec.subcategoryId ?? undefined,
            transferDestinationAccountId:
                rec.type === 'transfer' ? (rec.transferDestinationAccountId ?? undefined) : undefined,
            // Nasce pendente (sem carimbo — criação pendente não carimba) e vira paga
            // logo abaixo: quem carimba a hora é a virada, que enxerga a data REAL do
            // pagamento, não este nascimento no slot.
            isConfirmed: false,
            // Abater pela notificação não tira a ocorrência da série: herda o Ignorado (ADR 0169).
            isIgnored: rec.isIgnored === 1,
            isRecurring: true,
            recurringId: rec.id,
        });

    // Ocorrência criada agora (não existia) herda as tags da regra — mesma regra do
    // ensureOccurrence. Se já existia, mantém as tags dela.
    if (!existingOccurrence) {
        const ruleTagIds = (await getTagsForRecurring(rec.id)).map((tag) => tag.id);
        if (ruleTagIds.length > 0) {
            await setTransactionTags(occurrence.id, ruleTagIds);
        }
    }

    if (Object.keys(seriesPatch).length > 0) {
        await updateTransaction(occurrence.id, seriesPatch, mode);
    }

    // `time` entra pela porta 'single': é a hora do movimento que ACONTECEU, não um
    // campo da série — a regra continua com a hora dela pras próximas ocorrências.
    // A data real vai JUNTO da confirmação (e não num update depois): é a virada
    // pendente→pago que carimba a hora de quem não tem, e ela precisa enxergar o dia em
    // que o dinheiro se moveu — confirmar primeiro no slot futuro e só então trazer a
    // data pra hoje deixava a ocorrência sem hora.
    // O cartão vai JUNTO da data real pelo mesmo motivo da hora: a fatura sai da data em
    // que a compra aconteceu, não do slot agendado.
    await updateTransaction(
        occurrence.id,
        {
            isConfirmed: true,
            ...(actualDate !== targetDate ? { date: actualDate } : {}),
            ...(time ? { time } : {}),
            ...(creditCardId ? { creditCardId } : {}),
        },
        'single',
    );

    const refreshedOccurrence = await db
        .select()
        .from(transactions)
        .where(eq(transactions.id, occurrence.id));
    const activeRecurringId = refreshedOccurrence[0]?.recurringId ?? rec.id;

    if (targetDate === rec.nextOccurrence) {
        const activeRule = await db
            .select()
            .from(recurringTransactions)
            .where(
                and(
                    eq(recurringTransactions.id, activeRecurringId),
                    isNull(recurringTransactions.deletedAt),
                ),
            );

        if (activeRule[0]) {
            // Avanço ciente de skip: não deixa "próxima cobrança" num slot pulado.
            const skipped = parseSkippedDates(activeRule[0].skippedDates);
            const nextOccurrence = advancePastSkipped(
                activeRule[0],
                advanceOccurrence(targetDate, activeRule[0]),
                skipped,
            );
            const isActive = activeRule[0].endDate && nextOccurrence > activeRule[0].endDate ? 0 : 1;

            await db
                .update(recurringTransactions)
                .set({
                    nextOccurrence,
                    isActive,
                    updatedAt: new Date().toISOString(),
                })
                .where(eq(recurringTransactions.id, activeRule[0].id));
        }
    }

    return occurrence.id;
}

/**
 * Repara ocorrências DUPLICADAS no mesmo slot (mesma regra + mesma data agendada).
 *
 * Como a duplicata nasce: `generateOccurrences` materializa a partir de `nextOccurrence` e
 * deduplica só pelo que existe LOCALMENTE (findExistingOccurrence). Num aparelho com a base
 * ATRASADA — tablet aberto depois de duas semanas —, o ponteiro ainda aponta pra um slot que
 * OUTRO aparelho já materializou, confirmou e muitas vezes editou (salário de 5.558,72 que
 * caiu 5.784,60). O aparelho atrasado não vê essa linha, recria a ocorrência com o valor do
 * TEMPLATE e avança o ponteiro; o sync replica a linha nova e o slot fica com DUAS: a real
 * (confirmada/editada) e a fantasma (pendente, valor do template). O usuário lê isso como
 * "a antiga voltou".
 *
 * ONDE RODA: dentro do ciclo de sync, logo após aplicar dado remoto (lib/sync/sync.ts) — o
 * único instante em que uma duplicata ENTRA neste aparelho. Ali estamos sob o lock do sync,
 * e isso é requisito, não conveniência: durante um apply a `sync_guard` está setada e os
 * triggers de captura ficam MUDOS, então soft-delete emitido nessa janela não entraria na
 * outbox e o conserto morreria neste aparelho em vez de viajar. Fora do lock, o poll de 45s
 * podia abrir um apply exatamente por baixo destas escritas.
 *
 * Idempotente: sem duplicata não escreve nada.
 *
 * SOBREVIVENTE do slot, nesta ordem:
 *   1. CONFIRMADA vence pendente — é o lançamento real, com o valor que caiu de fato;
 *   2. EDITADA (updatedAt > createdAt) vence intocada — a fantasma nasce e morre com o valor
 *      do template, nunca é tocada. Sem este degrau, duas linhas CONFIRMADAS caíam direto no
 *      critério de idade e a correção que o usuário tivesse feito no aparelho novo era
 *      justamente a apagada;
 *   3. a mais ANTIGA — entre duas intocadas, a fantasma é sempre a recriada depois.
 */
export async function dedupeOccurrences(): Promise<number> {
    const slotExpr = sql<string>`COALESCE(${transactions.recurringOccurrenceDate}, ${transactions.date})`;

    // Sonda barata: só os slots que REALMENTE têm mais de uma linha viva. No caso normal
    // (nenhum) sai aqui sem carregar transação nenhuma pra memória nem abrir transação.
    const duplicatedSlots = await db
        .select({
            recurringId: transactions.recurringId,
            slot: slotExpr,
        })
        .from(transactions)
        .where(and(isNotNull(transactions.recurringId), isNull(transactions.deletedAt)))
        .groupBy(transactions.recurringId, slotExpr)
        .having(sql`count(*) > 1`);

    if (duplicatedSlots.length === 0) return 0;

    /** Linha tocada por gente: a fantasma sai do generateOccurrences com os dois iguais. */
    const wasEdited = (row: { createdAt: string; updatedAt: string }) =>
        (row.updatedAt > row.createdAt ? 1 : 0);

    let removed = 0;
    const affectedAccounts = new Set<string>();
    const affectedInvoices = new Set<string>();

    // Uma transação pro conjunto TODO: a conexão op-sqlite é única e o helper é o que serializa
    // contra o apply do sync (ver runInTransaction em db/client.ts). Um `db.update` solto aqui
    // podia cair DENTRO da transação de um apply em voo e sumir junto no rollback dele.
    // Os recálculos ficam de fora de propósito — são agregações pesadas e segurariam o mutex.
    await runInTransaction(async () => {
        for (const { recurringId, slot } of duplicatedSlots) {
            if (!recurringId) continue;

            const rows = await db
                .select()
                .from(transactions)
                .where(and(
                    eq(transactions.recurringId, recurringId),
                    eq(slotExpr, slot),
                    isNull(transactions.deletedAt),
                ));
            if (rows.length < 2) continue;

            const [, ...ghosts] = rows.sort((left, right) =>
                right.isConfirmed - left.isConfirmed
                || wasEdited(right) - wasEdited(left)
                || compareIso(left.createdAt, right.createdAt),
            );

            const now = new Date().toISOString();
            for (const ghost of ghosts) {
                await db
                    .update(transactions)
                    .set({ deletedAt: now, updatedAt: now })
                    .where(eq(transactions.id, ghost.id));
                if (ghost.accountId) affectedAccounts.add(ghost.accountId);
                if (ghost.transferDestinationAccountId) affectedAccounts.add(ghost.transferDestinationAccountId);
                if (ghost.invoiceId) affectedInvoices.add(ghost.invoiceId);
                removed += 1;
            }
        }
    });

    // Uma vez por conta/fatura, não por slot: o incidente duplicou OITO slots da mesma regra,
    // todos na mesma conta — recalcular por slot re-agregava aquele histórico oito vezes.
    for (const accountId of affectedAccounts) await recalculateAccountBalance(accountId);
    for (const invoiceId of affectedInvoices) await recalculateInvoiceTotal(invoiceId);

    return removed;
}

/**
 * Generate all pending occurrences for active recurring transactions.
 * Account-bound recurrings materialize up to today; credit-card recurrings
 * materialize through the end of the current month so the upcoming invoice
 * already reflects them (matches what the home projection counts).
 */
export async function generateOccurrences(): Promise<number> {
    const now = new Date();
    const today = format(now, 'yyyy-MM-dd');
    const monthEnd = format(endOfMonth(now), 'yyyy-MM-dd');
    let generated = 0;

    const pending = await db
        .select()
        .from(recurringTransactions)
        .where(
            and(
                eq(recurringTransactions.isActive, 1),
                isNull(recurringTransactions.deletedAt),
                lte(recurringTransactions.nextOccurrence, monthEnd),
            ),
        );

    for (const rec of pending) {
        const horizon = rec.creditCardId ? monthEnd : today;
        if (rec.nextOccurrence > horizon) continue;

        const skipped = parseSkippedDates(rec.skippedDates);
        let current = rec.nextOccurrence;

        while (current <= horizon) {
            // Check if endDate has been passed
            if (rec.endDate && current > rec.endDate) {
                // Deactivate the recurring
                await db
                    .update(recurringTransactions)
                    .set({ isActive: 0, updatedAt: new Date().toISOString() })
                    .where(eq(recurringTransactions.id, rec.id));
                break;
            }

            const existingOccurrence = await findExistingOccurrence(rec.id, current);
            if (!existingOccurrence && !skipped.has(current)) {
                await ensureOccurrence(rec, current, false);
                generated++;
            }
            current = advanceOccurrence(current, rec);
        }

        // Update nextOccurrence
        await db
            .update(recurringTransactions)
            .set({
                nextOccurrence: current,
                updatedAt: new Date().toISOString(),
            })
            .where(eq(recurringTransactions.id, rec.id));
    }

    return generated;
}

export async function getUpcomingExpenseOccurrences(
    fromDate: string,
    limit = 4,
): Promise<UpcomingRecurringExpense[]> {
    const recurringRows = await db
        .select({
            id: recurringTransactions.id,
            recurringId: recurringTransactions.id,
            amount: recurringTransactions.amount,
            description: recurringTransactions.description,
            nextOccurrence: recurringTransactions.nextOccurrence,
            accountId: recurringTransactions.accountId,
            accountName: accounts.name,
            accountColor: accounts.color,
            creditCardId: recurringTransactions.creditCardId,
            creditCardName: creditCards.name,
            creditCardColor: creditCards.color,
            categoryId: recurringTransactions.categoryId,
            categoryName: categories.name,
            categoryIcon: categories.icon,
            categoryColor: categories.color,
        })
        .from(recurringTransactions)
        .leftJoin(accounts, eq(recurringTransactions.accountId, accounts.id))
        .leftJoin(creditCards, eq(recurringTransactions.creditCardId, creditCards.id))
        .leftJoin(categories, eq(recurringTransactions.categoryId, categories.id))
        .where(
            and(
                eq(recurringTransactions.type, 'expense'),
                eq(recurringTransactions.isActive, 1),
                isNull(recurringTransactions.deletedAt),
                gte(recurringTransactions.nextOccurrence, fromDate),
            ),
        );

    const recurringIds = new Set(recurringRows.map((row) => row.id));

    const legacyRows = await db
        .select({
            id: transactions.id,
            recurringId: transactions.recurringId,
            amount: transactions.amount,
            description: transactions.description,
            nextOccurrence: transactions.date,
            accountId: transactions.accountId,
            accountName: accounts.name,
            accountColor: accounts.color,
            creditCardId: transactions.creditCardId,
            creditCardName: creditCards.name,
            creditCardColor: creditCards.color,
            categoryId: transactions.categoryId,
            categoryName: categories.name,
            categoryIcon: categories.icon,
            categoryColor: categories.color,
        })
        .from(transactions)
        .leftJoin(accounts, eq(transactions.accountId, accounts.id))
        .leftJoin(creditCards, eq(transactions.creditCardId, creditCards.id))
        .leftJoin(categories, eq(transactions.categoryId, categories.id))
        .where(
            and(
                eq(transactions.type, 'expense'),
                eq(transactions.isRecurring, 1),
                isNull(transactions.installmentId),
                isNull(transactions.deletedAt),
                gte(transactions.date, fromDate),
            ),
        );

    const upcoming: UpcomingRecurringExpense[] = recurringRows.map((row) => ({
        ...row,
        source: 'recurring',
    }));

    const legacyMap = new Map<string, UpcomingRecurringExpense>();
    for (const row of legacyRows) {
        if (row.recurringId && recurringIds.has(row.recurringId)) {
            continue;
        }

        const key = row.recurringId ?? `${row.description}:${row.accountId ?? ''}:${row.creditCardId ?? ''}`;
        const existing = legacyMap.get(key);

        if (!existing || row.nextOccurrence < existing.nextOccurrence) {
            legacyMap.set(key, {
                ...row,
                recurringId: row.recurringId ?? null,
                source: 'legacy_series',
            });
        }
    }

    return [...upcoming, ...legacyMap.values()]
        .sort((a, b) => compareIso(a.nextOccurrence, b.nextOccurrence))
        .slice(0, limit);
}
